import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, Phone } from "lucide-react";
import { API } from "@/lib/brand";
import { useAuth } from "@/lib/auth";
import { formatPublishedDate } from "@/lib/journal";

const TYPE_LABEL = {
  college: "College / University",
  corporate: "Corporate / Org",
  community: "Community Event",
  other: "Other",
};

const TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "college", label: "College" },
  { value: "corporate", label: "Corporate" },
  { value: "community", label: "Community" },
  { value: "other", label: "Other" },
];

const STATUS_ORDER = ["new", "contacted", "scheduled", "done"];

const STATUS_LABEL = {
  new: "New",
  contacted: "Contacted",
  scheduled: "Scheduled",
  done: "Done",
};

const STATUS_STYLES = {
  new: "bg-pink/15 text-pink-deep border-pink/40",
  contacted: "bg-brass/15 text-brass-deep border-brass/40",
  scheduled: "bg-teal/15 text-teal-deep border-teal/40",
  done: "bg-paper-raised text-ink-soft border-line",
};

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
];

export default function AdminCompass() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/journal/admin/compass`, { credentials: "include" });
        if (res.ok) {
          setRows(await res.json());
        } else {
          toast.error("Failed to load Compass requests");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateStatus = async (row, next) => {
    if (row.status === next) return;
    setSavingId(row.id);
    // Optimistic update
    const prev = row.status;
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    try {
      const res = await fetch(`${API}/journal/admin/compass/${row.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error("Update failed");
      toast.success(`Marked as ${STATUS_LABEL[next].toLowerCase()}`);
    } catch (e) {
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, status: prev } : r)));
      toast.error("Couldn't update status");
    } finally {
      setSavingId(null);
    }
  };

  const typeCounts = useMemo(() => {
    const counts = { all: rows.length, college: 0, corporate: 0, community: 0, other: 0 };
    rows.forEach((r) => {
      if (counts[r.type] !== undefined) counts[r.type] += 1;
    });
    return counts;
  }, [rows]);

  const statusCounts = useMemo(() => {
    const counts = { all: rows.length, new: 0, contacted: 0, scheduled: 0, done: 0 };
    rows.forEach((r) => {
      const s = r.status || "new";
      if (counts[s] !== undefined) counts[s] += 1;
    });
    return counts;
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (typeFilter !== "all" && r.type !== typeFilter) return false;
      const s = r.status || "new";
      if (statusFilter !== "all" && s !== statusFilter) return false;
      return true;
    });
  }, [rows, typeFilter, statusFilter]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-16" data-testid="admin-compass-page">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Journal · Admin</div>
          <h1 className="font-display text-3xl md:text-4xl text-ink leading-tight">
            Compass requests
          </h1>
          {user && (
            <p className="text-ink-soft text-[13px] mt-2 num">
              Signed in as {user.email} · {rows.length} total
            </p>
          )}
        </div>
        <Link to="/journal/admin" className="btn-outline" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Back to posts
        </Link>
      </div>

      {/* Filters */}
      {!loading && rows.length > 0 && (
        <div className="space-y-3 mb-6" data-testid="compass-filters">
          <FilterRow
            eyebrow="Type"
            options={TYPE_FILTERS}
            counts={typeCounts}
            active={typeFilter}
            onChange={setTypeFilter}
            testidPrefix="type"
          />
          <FilterRow
            eyebrow="Status"
            options={STATUS_FILTERS}
            counts={statusCounts}
            active={statusFilter}
            onChange={setStatusFilter}
            testidPrefix="status"
          />
        </div>
      )}

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-soft">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="card-paper p-10 text-center">
          <p className="text-ink-soft">No Compass requests yet.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-paper p-10 text-center">
          <p className="text-ink-soft">
            No matches for this filter.{" "}
            <button
              className="link-brass"
              onClick={() => { setTypeFilter("all"); setStatusFilter("all"); }}
            >
              Clear filters
            </button>
          </p>
        </div>
      ) : (
        <div className="border hairline rounded-md overflow-x-auto">
          <table className="w-full text-[13.5px]" data-testid="admin-compass-table">
            <thead>
              <tr className="border-b hairline bg-paper-raised text-left">
                <th className="eyebrow px-4 py-3 whitespace-nowrap">Received</th>
                <th className="eyebrow px-4 py-3">Name · Institution</th>
                <th className="eyebrow px-4 py-3">Contact</th>
                <th className="eyebrow px-4 py-3">Type</th>
                <th className="eyebrow px-4 py-3">Dates · Size</th>
                <th className="eyebrow px-4 py-3">Topic</th>
                <th className="eyebrow px-4 py-3 whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const status = r.status || "new";
                const isSaving = savingId === r.id;
                return (
                  <tr key={r.id} className="border-b hairline last:border-b-0 align-top">
                    <td className="px-4 py-3 num text-ink-soft text-[12px] whitespace-nowrap">
                      {formatPublishedDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-ink font-medium">{r.name}</div>
                      <div className="text-ink-soft text-[12px] mt-0.5">{r.institution}</div>
                    </td>
                    <td className="px-4 py-3 num text-[12px]">
                      <a
                        href={`mailto:${r.email}`}
                        className="inline-flex items-center gap-1.5 text-ink hover:text-brass-deep"
                      >
                        <Mail size={12} /> {r.email}
                      </a>
                      <a
                        href={`tel:${r.phone}`}
                        className="inline-flex items-center gap-1.5 text-ink-soft hover:text-ink mt-1"
                      >
                        <Phone size={12} /> {r.phone}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-ink-soft whitespace-nowrap">
                      {TYPE_LABEL[r.type] || r.type}
                    </td>
                    <td className="px-4 py-3 text-ink-soft text-[12.5px]">
                      <div>{r.preferred_dates || "—"}</div>
                      <div className="num text-[11.5px] mt-0.5">
                        {r.audience_size ? `${r.audience_size} attendees` : "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-soft text-[12.5px] max-w-sm">
                      <div className="line-clamp-4 whitespace-pre-wrap">{r.message || "—"}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="relative inline-flex items-center">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-[11px] border capitalize ${STATUS_STYLES[status]}`}
                        >
                          {STATUS_LABEL[status]}
                        </span>
                        <select
                          value={status}
                          onChange={(e) => updateStatus(r, e.target.value)}
                          disabled={isSaving}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full"
                          data-testid={`status-select-${r.id}`}
                          aria-label="Update status"
                        >
                          {STATUS_ORDER.map((s) => (
                            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                          ))}
                        </select>
                        {isSaving && (
                          <Loader2 size={12} className="animate-spin text-ink-soft ml-2" />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterRow({ eyebrow, options, counts, active, onChange, testidPrefix }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="eyebrow text-[11px] text-ink-soft w-20 shrink-0">{eyebrow}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const isActive = active === o.value;
          const count = counts[o.value] ?? 0;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              disabled={count === 0 && o.value !== "all"}
              className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] border transition-colors " +
                (isActive
                  ? "bg-ink text-paper border-ink"
                  : "bg-paper text-ink-soft border-line hover:text-ink hover:border-ink/40 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-ink-soft disabled:hover:border-line")
              }
              data-testid={`filter-${testidPrefix}-${o.value}`}
            >
              <span>{o.label}</span>
              <span className={"num text-[10.5px] " + (isActive ? "text-paper/70" : "text-ink-soft")}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
